import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { CaptchaFieldFragmentFragment, GetReCaptchaKeyQuery } from '../../../../__generated__/graphql';
import { gql } from '../../../../wp-templates/helpers';
import GetReCaptchaKey from '../../../../wp-templates/recaptchaKey.gql';
import { useGravityFormsDispatch, useSelectCaptchaFieldValue, useSelectInitialized } from '../../state/hooks';
import ConditionalWrapper from '../ConditionalWrapper';
import FieldErrors from '../FieldErrors';
import FormFieldGridContainer from '../FormFieldGridContainer';

interface Props {
  field: CaptchaFieldFragmentFragment;
}

export const recaptchaInputId = 'recpatcha-hidden-input';

const CaptchaInput = ({ field }: Props) => {
  const { data } = useQuery<GetReCaptchaKeyQuery>(gql(GetReCaptchaKey));
  const {
    conditionalLogic,
    databaseId,
    layoutGridColumnSpan,
  } = field;

  const [recaptchaId, setRecaptchaId] = useState<null | number>(null);

  const dispatch = useGravityFormsDispatch();

  const fieldValue = useSelectCaptchaFieldValue(databaseId);
  const initialized = useSelectInitialized();

  const id = `g-recaptcha-${databaseId.toString()}`;
  const captchaKey = data?.gfSettings?.recaptcha?.publicKey ?? null;

  useEffect(() => {
    if (recaptchaId == null && initialized && fieldValue && captchaKey) {
      const handleUpdateValue = (value: string) => {
        const recaptchaInput = document.getElementById(recaptchaInputId) as HTMLInputElement | null;
        if (recaptchaInput) {
          recaptchaInput.value = value;
        }

        dispatch({
          payload: {
            id: databaseId,
            value,
          },
          type: 'updateCaptchaFieldValue',
        });
      };

      const captchaId = window.grecaptcha?.render(id, {
        callback: handleUpdateValue,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'expired-callback': () => {
          handleUpdateValue('');
        },
        sitekey: captchaKey,
      });

      if (captchaId != null) {
        setRecaptchaId(captchaId);
      }
    }
  }, [databaseId, dispatch, id, recaptchaId, initialized, fieldValue, captchaKey]);

  if (initialized && !fieldValue) {
    return null;
  }

  return (
    <ConditionalWrapper conditionalLogic={conditionalLogic}>
      <FormFieldGridContainer layoutGridColumnSpan={layoutGridColumnSpan}>
        <div id={id} />

        <FieldErrors fieldId={databaseId} />

        {/* this hidden input is only here for validation */}
        {/* see GravityFormsForm.handleSubmit */}
        <input
          data-recaptcha-database-id={databaseId.toString()}
          id="recpatcha-hidden-input"
          type="hidden"
        />
      </FormFieldGridContainer>
    </ConditionalWrapper>
  );
};

export default CaptchaInput;
